import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface InductionData {
  id: string;
  worker?: {
    name: string;
    role: string;
    email: string;
    phone?: string;
    certifications?: string[];
  };
  status: string;
  completed_at?: string;
  created_at: string;
  signature?: string;
}

interface SiteData {
  id: string;
  name: string;
  type: string;
  address: string;
  requirements?: string[];
  description?: string;
}

interface SiteInductionsData {
  site: SiteData;
  inductions: InductionData[];
  completedCount: number;
  pendingCount: number;
  totalCount: number;
}

export const generateSiteInductionsPDF = async (siteData: SiteInductionsData): Promise<void> => {
  const completedInductions = siteData.inductions.filter(induction => induction.status === 'completed');
  
  if (completedInductions.length === 0) {
    alert('No completed inductions found for this site.');
    return;
  }

  // Create a temporary container for the PDF content
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = '794px'; // A4 width in pixels at 96 DPI
  container.style.backgroundColor = 'white';
  container.style.padding = '30px';
  container.style.fontFamily = 'Arial, sans-serif';
  container.style.lineHeight = '1.4';
  container.style.fontSize = '12px';
  
  // Create the HTML content
  container.innerHTML = `
    <div style="max-width: 734px; margin: 0 auto;">
      <!-- Header -->
      <div style="text-align: center; margin-bottom: 25px; border-bottom: 3px solid #ea580c; padding-bottom: 20px;">
        <div style="display: flex; align-items: center; justify-content: center; margin-bottom: 15px;">
          <div style="width: 50px; height: 50px; background-color: #fed7aa; border-radius: 10px; display: flex; align-items: center; justify-content: center; margin-right: 15px;">
            <svg width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="#ea580c" stroke-width="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9,22 9,12 15,12 15,22"/>
            </svg>
          </div>
          <div>
            <h1 style="font-size: 26px; font-weight: bold; color: #1f2937; margin: 0; line-height: 1.1;">SITE SAFETY INDUCTION REPORT</h1>
            <p style="font-size: 14px; color: #6b7280; margin: 5px 0 0 0;">Comprehensive Safety Compliance Documentation</p>
          </div>
        </div>
      </div>

      <!-- Site Information Banner -->
      <div style="background: linear-gradient(135deg, #f97316 0%, #ea580c 100%); color: white; border-radius: 10px; padding: 20px; margin-bottom: 25px;">
        <div style="display: grid; grid-template-columns: 2fr 1fr; gap: 20px; align-items: center;">
          <div>
            <h2 style="font-size: 22px; font-weight: bold; margin: 0 0 8px 0;">${siteData.site.name}</h2>
            <p style="font-size: 14px; margin: 0 0 5px 0; opacity: 0.9;">📍 ${siteData.site.address}</p>
            <p style="font-size: 13px; margin: 0; opacity: 0.8; text-transform: capitalize;">
              ${siteData.site.type.replace('_', ' ')} Project
            </p>
          </div>
          <div style="text-align: right;">
            <div style="background-color: rgba(255,255,255,0.2); border-radius: 8px; padding: 12px;">
              <p style="font-size: 24px; font-weight: bold; margin: 0; line-height: 1;">${completedInductions.length}</p>
              <p style="font-size: 12px; margin: 0; opacity: 0.9;">Completed Inductions</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary Statistics -->
      <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 25px;">
        <div style="background-color: #f0f9ff; border: 1px solid #0ea5e9; border-radius: 8px; padding: 15px; text-align: center;">
          <p style="font-size: 20px; font-weight: bold; color: #0369a1; margin: 0 0 5px 0;">${siteData.totalCount}</p>
          <p style="font-size: 11px; color: #0369a1; margin: 0; font-weight: 600;">Total Inductions</p>
        </div>
        <div style="background-color: #f0fdf4; border: 1px solid #22c55e; border-radius: 8px; padding: 15px; text-align: center;">
          <p style="font-size: 20px; font-weight: bold; color: #16a34a; margin: 0 0 5px 0;">${siteData.completedCount}</p>
          <p style="font-size: 11px; color: #16a34a; margin: 0; font-weight: 600;">Completed</p>
        </div>
        <div style="background-color: #fffbeb; border: 1px solid #f59e0b; border-radius: 8px; padding: 15px; text-align: center;">
          <p style="font-size: 20px; font-weight: bold; color: #d97706; margin: 0 0 5px 0;">${siteData.pendingCount}</p>
          <p style="font-size: 11px; color: #d97706; margin: 0; font-weight: 600;">Pending</p>
        </div>
        <div style="background-color: #f8fafc; border: 1px solid #64748b; border-radius: 8px; padding: 15px; text-align: center;">
          <p style="font-size: 20px; font-weight: bold; color: #475569; margin: 0 0 5px 0;">${Math.round((siteData.completedCount / siteData.totalCount) * 100)}%</p>
          <p style="font-size: 11px; color: #475569; margin: 0; font-weight: 600;">Completion Rate</p>
        </div>
      </div>

      ${siteData.site.requirements && siteData.site.requirements.length > 0 ? `
        <!-- Site Safety Requirements -->
        <div style="background-color: #fef3c7; border: 1px solid #f59e0b; border-radius: 8px; padding: 18px; margin-bottom: 25px;">
          <h3 style="font-size: 16px; font-weight: 600; color: #92400e; margin: 0 0 12px 0; display: flex; align-items: center;">
            <span style="margin-right: 8px;">⚠️</span> Site Safety Requirements
          </h3>
          <p style="font-size: 11px; color: #92400e; margin: 0; line-height: 1.4; font-weight: 500;">
            ${siteData.site.requirements.join(' - ')}
          </p>
        </div>
      ` : ''}

      <!-- Completed Inductions List -->
      <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 20px; margin-bottom: 25px;">
        <h3 style="font-size: 18px; font-weight: 600; color: #1f2937; margin: 0 0 18px 0; display: flex; align-items: center;">
          <span style="margin-right: 8px;">✅</span> Completed Safety Inductions
        </h3>
        
        <div style="display: grid; gap: 12px;">
          ${completedInductions.map((induction, index) => `
            <div style="background-color: white; border: 1px solid #e5e7eb; border-radius: 6px; padding: 15px;">
              <div style="display: grid; grid-template-columns: auto 1fr auto auto; gap: 15px; align-items: center;">
                
                <!-- Index Number -->
                <div style="width: 30px; height: 30px; background-color: #dcfce7; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
                  <span style="font-size: 12px; font-weight: bold; color: #166534;">${index + 1}</span>
                </div>
                
                <!-- Worker Information -->
                <div>
                  <p style="font-size: 14px; font-weight: 600; color: #1f2937; margin: 0 0 3px 0;">${induction.worker?.name || 'Unknown Worker'}</p>
                  <p style="font-size: 11px; color: #6b7280; margin: 0 0 2px 0; text-transform: capitalize;">${induction.worker?.role?.replace('_', ' ') || 'N/A'}</p>
                  <p style="font-size: 10px; color: #9ca3af; margin: 0;">${induction.worker?.email || 'No email'}</p>
                  ${induction.worker?.phone ? `<p style="font-size: 10px; color: #9ca3af; margin: 0;">📞 ${induction.worker.phone}</p>` : ''}
                  ${induction.worker?.whitecard_number ? `<p style="font-size: 9px; color: #374151; margin: 2px 0 0 0; font-family: monospace; background-color: #f3f4f6; padding: 1px 3px; border-radius: 2px; display: inline-block;">WC: ${induction.worker.whitecard_number}</p>` : ''}
                  ${induction.worker?.whitecard_number ? `<p style="font-size: 9px; color: #374151; margin: 2px 0 0 0;">WC: ${induction.worker.whitecard_number}</p>` : ''}
                  ${induction.worker?.start_date ? `<p style="font-size: 9px; color: #6b7280; margin: 1px 0 0 0;">Started: ${new Date(induction.worker.start_date).toLocaleDateString()}</p>` : ''}
                  ${induction.worker?.certifications && induction.worker.certifications.length > 0 ? `
                    <div style="margin-top: 4px;">
                      <p style="font-size: 8px; color: #374151; margin: 0; line-height: 1.3;">
                        ${induction.worker.certifications.join(' - ')}
                      </p>
                    </div>
                  ` : ''}
                </div>
                
                <!-- Completion Date -->
                <div style="text-align: center;">
                  <p style="font-size: 11px; font-weight: 600; color: #374151; margin: 0 0 2px 0;">Completed</p>
                  <p style="font-size: 10px; color: #6b7280; margin: 0;">${induction.completed_at ? new Date(induction.completed_at).toLocaleDateString() : 'N/A'}</p>
                  <p style="font-size: 9px; color: #9ca3af; margin: 1px 0 0 0;">Created: ${new Date(induction.created_at).toLocaleDateString()}</p>
                </div>
                
                <!-- Status Badge -->
                <div>
                  <span style="font-size: 10px; font-weight: 600;">
                    ✓ Certified
                  </span>
                </div>
              </div>
              
              <!-- Signature Preview (if available) -->
              ${induction.signature ? `
                <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #f3f4f6;">
                  <div style="display: flex; align-items: center; justify-content: space-between;">
                    <span style="font-size: 10px; color: #6b7280; font-weight: 500;">Digital Signature:</span>
                    <div style="background-color: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; padding: 4px 8px; max-width: 150px; height: 25px; display: flex; align-items: center; justify-content: center;">
                      ${induction.signature.startsWith('data:image') ? 
                        `<img src="${induction.signature}" alt="Signature" style="max-width: 100%; max-height: 20px;" />` :
                        `<span style="font-size: 9px; color: #374151; font-style: italic;">${induction.signature.substring(0, 20)}${induction.signature.length > 20 ? '...' : ''}</span>`
                      }
                    </div>
                  </div>
                </div>
              ` : ''}
            </div>
          `).join('')}
        </div>
      </div>

      <!-- Compliance Summary -->
      <div style="background-color: #f0fdf4; border: 1px solid #22c55e; border-radius: 8px; padding: 18px; margin-bottom: 20px;">
        <h3 style="font-size: 16px; font-weight: 600; color: #166534; margin: 0 0 12px 0; display: flex; align-items: center;">
          <span style="margin-right: 8px;">🛡️</span> Safety Compliance Summary
        </h3>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px;">
          <div>
            <p style="font-size: 12px; color: #166534; margin: 0 0 8px 0; font-weight: 600;">✓ All workers have completed mandatory safety training</p>
            <p style="font-size: 12px; color: #166534; margin: 0 0 8px 0; font-weight: 600;">✓ Site-specific hazards have been communicated</p>
            <p style="font-size: 12px; color: #166534; margin: 0; font-weight: 600;">✓ Emergency procedures have been explained</p>
          </div>
          <div>
            <p style="font-size: 12px; color: #166534; margin: 0 0 8px 0; font-weight: 600;">✓ PPE requirements have been verified</p>
            <p style="font-size: 12px; color: #166534; margin: 0 0 8px 0; font-weight: 600;">✓ Work method statements reviewed</p>
            <p style="font-size: 12px; color: #166534; margin: 0; font-weight: 600;">✓ Digital signatures captured for verification</p>
          </div>
        </div>
        
        <!-- Additional Site Information -->
        <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #e5e7eb;">
          <h4 style="font-size: 12px; font-weight: 600; color: #166534; margin: 0 0 8px 0;">Site Details Summary</h4>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; font-size: 10px; color: #374151;">
            <div>
              <strong>Project Duration:</strong><br>
              ${siteData.site.start_date ? new Date(siteData.site.start_date).toLocaleDateString() : 'N/A'} - 
              ${siteData.site.end_date ? new Date(siteData.site.end_date).toLocaleDateString() : 'N/A'}
            </div>
            <div>
              <strong>Site Status:</strong><br>
              <span style="text-transform: capitalize;">${siteData.site.status || 'Unknown'}</span>
            </div>
            ${siteData.site.asbestos_type && siteData.site.asbestos_type !== 'none' ? `
              <div>
                <strong>Asbestos Present:</strong><br>
                <span style="text-transform: capitalize; color: #dc2626;">${siteData.site.asbestos_type.replace('_', ' ')}</span>
              </div>
            ` : ''}
            <div>
              <strong>Total Workers Inducted:</strong><br>
              ${siteData.completedCount} of ${siteData.totalCount} workers
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div style="text-align: center; padding-top: 20px; border-top: 2px solid #e5e7eb;">
        <p style="font-size: 11px; color: #6b7280; margin: 0 0 5px 0; line-height: 1.4;">
          <strong>Site Safety Induction Report</strong><br>
          This document certifies that all listed workers have successfully completed site-specific safety training<br>
          and are authorized to work on the above-mentioned project site.
        </p>
        <p style="font-size: 9px; color: #9ca3af; margin: 0; line-height: 1.3;">
          Generated: ${new Date().toLocaleString()} | SafetyPortal Induction Management System<br>
          Report ID: ${siteData.site.id} | Total Pages: 1
        </p>
      </div>
    </div>
  `;

  // Add the container to the document
  document.body.appendChild(container);

  try {
    // Convert the HTML to canvas with optimized settings
    const canvas = await html2canvas(container, {
      scale: 2, // Higher quality
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width: container.offsetWidth,
      height: container.offsetHeight,
      scrollX: 0,
      scrollY: 0,
      windowWidth: container.offsetWidth,
      windowHeight: container.offsetHeight
    });

    // Create PDF with A4 dimensions
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // A4 dimensions in mm
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 10;
    const contentWidth = pageWidth - (margin * 2);
    const contentHeight = pageHeight - (margin * 2);

    // Calculate scaling to fit the content
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    
    // Scale to fit both width and height within the page
    const scaleX = (contentWidth * 3.78) / canvasWidth; // 3.78 is mm to px conversion
    const scaleY = (contentHeight * 3.78) / canvasHeight;
    const scale = Math.min(scaleX, scaleY); // Use the smaller scale to ensure it fits

    const scaledWidth = (canvasWidth * scale) / 3.78;
    const scaledHeight = (canvasHeight * scale) / 3.78;

    // Center the content on the page
    const xOffset = margin + (contentWidth - scaledWidth) / 2;
    const yOffset = margin;

    // Add the image to the PDF
    const imgData = canvas.toDataURL('image/png');
    pdf.addImage(
      imgData,
      'PNG',
      xOffset,
      yOffset,
      scaledWidth,
      scaledHeight
    );

    // Generate filename
    const siteName = siteData.site.name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
    const filename = `site-inductions-${siteName}-${new Date().toISOString().split('T')[0]}.pdf`;

    // Download the PDF
    pdf.save(filename);

  } finally {
    // Clean up - remove the temporary container
    document.body.removeChild(container);
  }
};